import { Component, inject } from '@angular/core';

import {
  HLM_ADMIN_DATA_TABLE_VARIANT,
  HlmTableImports,
} from '@spartan-ng/helm/table';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { AppLoadingService } from '@src/app/core/services/app-loading.service';
import { AppRepository } from '@src/app/app.repository';

@Component({
  selector: 'app-skeleton-with-progress',
  standalone: true,
  imports: [HlmSkeletonImports, HlmTableImports, HlmSpinnerImports],
  template: `
    <div class="flex min-h-screen flex-col bg-background">
      <!-- Progress bar -->
      <div class="fixed top-0 left-0 right-0 z-50">
        <div
          class="h-1 bg-primary transition-all duration-300 ease-out"
          [style.width.%]="loadingService.loadingProgress()"
        ></div>
      </div>

      <!-- Mobile header -->
      <div
        class="fixed top-0 left-0 right-0 z-30 flex w-full items-center justify-between border-b border-border bg-background px-4 py-2 lg:hidden"
      >
        <hlm-skeleton class="h-6 w-32" />
        <hlm-skeleton class="h-8 w-8 rounded-full" />
      </div>

      <!-- Shell: mini sidebar + main (matches shell.component: 60px sidebar, then content) -->
      <div
        class="flex h-screen w-screen max-h-screen max-w-screen overflow-hidden pt-[37px] lg:pt-0"
      >
        <!-- Mini sidebar (shell) - 56px like --mini-sidebar-width -->
        <aside
          class="fixed top-0 bottom-0 left-0 z-20 hidden w-14 flex-col items-center gap-5 border-r border-sidebar-border bg-sidebar py-5 lg:flex"
        >
          <hlm-skeleton class="h-10 w-10 shrink-0 rounded" />
          <div class="flex-1"></div>
          <hlm-skeleton class="h-9 w-9 shrink-0 rounded-md" />
          <hlm-skeleton class="h-9 w-9 shrink-0 rounded-full" />
        </aside>

        <!-- Content area: admin side menu (left) + page content (right) on the
             sidebar-tinted canvas, like admin.component -->
        <div
          class="flex min-w-0 flex-1 flex-row bg-sidebar lg:ml-14"
        >
          <!-- Admin side menu (280px expanded / 56px icon rail, matching the
               persisted collapse state so the skeleton doesn't jump) -->
          <nav
            class="hidden h-full shrink-0 flex flex-col overflow-y-auto bg-sidebar lg:flex"
            [style.width.px]="sidebarWidth"
          >
            @if (sidebarWidth < 100) {
              <div class="flex flex-col items-center gap-2 py-4">
                <hlm-skeleton class="h-8 w-8 shrink-0 rounded-md" />
                @for (i of [1, 2, 3, 4, 5, 6, 7, 8]; track i) {
                  <hlm-skeleton class="h-9 w-9 shrink-0 rounded-md" />
                }
              </div>
            }
            <div class="space-y-1 p-4" [class.hidden]="sidebarWidth < 100">
              <div class="mb-6">
                <hlm-skeleton class="mb-2 h-4 w-20" />
                <hlm-skeleton class="h-9 w-full rounded-md" />
              </div>
              <div class="mb-6 space-y-1">
                <hlm-skeleton class="mb-2 h-4 w-16" />
                @for (i of [1, 2, 3, 4]; track i) {
                  <div class="flex items-center gap-3 rounded-md px-2 py-2">
                    <hlm-skeleton class="h-4 w-4 shrink-0" />
                    <hlm-skeleton class="h-4 w-24" />
                  </div>
                }
              </div>
              <div class="mt-6 space-y-1">
                <hlm-skeleton class="mb-2 h-4 w-20" />
                @for (i of [1, 2, 3]; track i) {
                  <div class="flex items-center gap-3 rounded-md px-2 py-2">
                    <hlm-skeleton class="h-4 w-4 shrink-0" />
                    <hlm-skeleton class="h-4 w-20" />
                  </div>
                }
              </div>
            </div>
          </nav>

          <!-- Page content: floating inset card on the sidebar canvas, like
               admin.component's default (non-customCanvas) recipe -->
          <main class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-sidebar">
            <section
              class="flex min-h-0 flex-1 flex-col overflow-hidden bg-background lg:m-2 lg:rounded-[0.875rem] lg:shadow-sm"
            >
              <!-- Page header: breadcrumb + title + toolbar -->
              <header
                class="flex shrink-0 flex-col gap-1 px-4 py-4 md:px-8 md:py-5"
              >
                <nav class="flex items-center gap-1.5">
                  <hlm-skeleton class="h-4 w-24" />
                  <hlm-skeleton class="h-3 w-3 rounded-sm" />
                  <hlm-skeleton class="h-4 w-20" />
                  <hlm-skeleton class="h-3 w-3 rounded-sm" />
                  <hlm-skeleton class="h-4 w-28" />
                </nav>
                <hlm-skeleton class="h-7 w-48 sm:h-8 sm:w-56" />
                <div
                  class="flex flex-col gap-3 pt-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div class="flex gap-2">
                    <hlm-skeleton class="h-9 w-[180px] sm:w-[240px]" />
                    <hlm-skeleton class="h-9 w-9 shrink-0 lg:hidden" />
                  </div>
                  <div class="flex gap-2">
                    <hlm-skeleton class="h-9 w-24" />
                    <hlm-skeleton class="h-9 w-28" />
                  </div>
                </div>
              </header>

              <!-- Table panel (HLM_ADMIN_DATA_TABLE_VARIANT + sticky thead) -->
              <div class="flex min-h-0 min-w-0 min-h-[360px] flex-1 flex-col">
                <div
                  class="min-h-0 min-w-0 w-full flex-1 overflow-auto px-4 md:px-8"
                >
                  <table
                    class="w-full min-w-[640px]"
                    [hlmTable]="adminDataTableVariant"
                  >
                    <thead hlmTHead class="hlm-admin-data-table-thead">
                      <tr hlmTr>
                        @for (c of [1, 2, 3, 4, 5, 6]; track c) {
                          <th hlmTh>
                            <hlm-skeleton class="h-4 w-16" />
                          </th>
                        }
                      </tr>
                    </thead>
                    <tbody hlmTBody class="w-full">
                      @for (row of [1, 2, 3, 4, 5, 6, 7, 8]; track row) {
                        <tr hlmTr>
                          <td hlmTd>
                            <hlm-skeleton class="h-4 w-4 shrink-0 rounded-sm" />
                          </td>
                          <td hlmTd class="py-4">
                            <hlm-skeleton class="h-4 w-32" />
                          </td>
                          <td hlmTd class="py-4">
                            <hlm-skeleton class="h-4 w-24" />
                          </td>
                          <td hlmTd class="py-4">
                            <hlm-skeleton class="h-4 w-20" />
                          </td>
                          <td hlmTd class="py-4">
                            <hlm-skeleton class="h-4 w-28" />
                          </td>
                          <td hlmTd class="py-4">
                            <hlm-skeleton class="h-4 w-16" />
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
                <!-- <div
                  class="flex shrink-0 flex-col justify-between gap-2 border-t border-border bg-muted/50 px-4 py-3 sm:flex-row sm:items-center md:px-8"
                >
                  <hlm-skeleton class="h-4 w-40" />
                  <div class="flex gap-2">
                    <hlm-skeleton class="h-8 w-8 rounded-md" />
                    <hlm-skeleton class="h-8 w-16 rounded-md" />
                    <hlm-skeleton class="h-8 w-8 rounded-md" />
                  </div>
                </div> -->
              </div>
            </section>
          </main>
        </div>
      </div>

      <!-- Loading footer -->
      <!-- <div class="shrink-0 border-t border-border bg-background px-3 py-3">
        <div class="mx-auto flex max-w-7xl items-center justify-between">
          <div class="flex items-center gap-3">
            <hlm-spinner class="size-4 shrink-0 text-primary" />
            <span class="text-sm text-muted-foreground">
              {{ loadingService.loadingMessage() }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-muted-foreground">
              {{ loadingService.loadingProgress() }}%
            </span>
            <div class="relative h-5 w-5">
              <svg class="h-5 w-5 -rotate-90" viewBox="0 0 20 20">
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  class="text-muted-foreground/20"
                />
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  class="text-primary transition-all duration-300 ease-out"
                  [style.stroke-dasharray]="2 * Math.PI * 8"
                  [style.stroke-dashoffset]="2 * Math.PI * 8 * (1 - loadingService.loadingProgress() / 100)"
                />
              </svg>
            </div>
          </div>
        </div>
      </div> -->
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100vh;
        overflow: hidden;
      }
    `,
  ],
})
export class AppSkeletonWithProgressComponent {
  protected readonly adminDataTableVariant = HLM_ADMIN_DATA_TABLE_VARIANT;
  protected readonly loadingService = inject(AppLoadingService);
  protected readonly sidebarWidth = inject(AppRepository)
    .isDesktopSiderCollapsed
    ? 56
    : 280;
  protected readonly Math = Math;
}
